import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";

/**
 * Subtle PWA-install hint banner. Shows once per device.
 *
 * Android (Chrome / Edge / Brave): captures the native `beforeinstallprompt`
 * event and renders an "Install app" button that triggers it directly.
 *
 * iOS Safari: Apple blocks `beforeinstallprompt`, so we render manual
 * instructions ("Share → Add to Home Screen") only when the user is on
 * an iOS device and the page isn't already running as a standalone app.
 *
 * Dismissable per-device via localStorage. Never re-shows once acted on.
 */
const DISMISS_KEY = "ydd_install_hint_dismissed_v1";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS uses `navigator.standalone`; everyone else honors the media query.
  return window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
}

export default function InstallHint() {
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  });

  useEffect(() => {
    if (dismissed) return;
    if (isStandalone()) return;  // already installed → never prompt

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS path — Safari never fires `beforeinstallprompt`.
    if (isIOS()) setShowIOS(true);

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [dismissed]);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, "1"); }
    catch (e) { console.debug("InstallHint: localStorage write failed (private browsing?)", e); }
    setDismissed(true);
    setDeferred(null);
    setShowIOS(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      dismiss();
    }
  };

  if (dismissed) return null;
  if (!deferred && !showIOS) return null;

  return (
    <div
      data-testid="install-hint"
      role="status"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-50 bg-card border border-border shadow-xl rounded-xl p-4 flex items-start gap-3"
    >
      <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
        <Plus size={18} />
      </div>
      <div className="flex-1 min-w-0">
        {deferred ? (
          <>
            <p className="font-medium text-sm">Install Your Dog Days</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add this app to your home screen for one-tap access and offline support.
            </p>
            <button
              type="button"
              onClick={install}
              data-testid="install-hint-cta"
              className="mt-2.5 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium px-3 py-1.5 rounded-md hover:bg-primary/90 transition-colors"
            >
              Install app
            </button>
          </>
        ) : (
          <>
            <p className="font-medium text-sm">Add to Home Screen</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Tap <Share size={12} className="inline -mt-0.5" /> Share in Safari, then choose <strong>Add to Home Screen</strong>.
            </p>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="install-hint-dismiss"
        aria-label="Dismiss install hint"
        className="p-1 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0 -mr-1 -mt-1"
      >
        <X size={16} />
      </button>
    </div>
  );
}
